import type { ApiClient } from '../client/api-client';
import type {
  ComplianceBlockedAddress,
  ComplianceDecision,
  ComplianceDecisionPayload,
  ComplianceMessagesResponse,
  ComplianceQueueResponse,
  ComplianceResolvePayload,
  ComplianceReviewer,
  ComplianceReviewerUpdate,
  ComplianceWithdrawalCard,
} from '../contracts/compliance';
import type { SecondFactor } from '../contracts/security';

const idPath = (id: string | number) => encodeURIComponent(String(id));

export function createComplianceService(client: ApiClient) {
  return {
    listWithdrawals: (params: { status?: string; page?: number; limit?: number } = {}) => {
      const search = new URLSearchParams();
      search.set('page', String(params.page ?? 1));
      search.set('limit', String(params.limit ?? 20));
      if (params.status) search.set('status', params.status);
      return client.get<ComplianceQueueResponse>(`/app/v1/admin/compliance/withdrawals?${search.toString()}`);
    },
    getWithdrawal: (id: string | number) =>
      client.get<ComplianceWithdrawalCard>(`/app/v1/admin/compliance/withdrawals/${idPath(id)}`),
    decideWithdrawal: (id: string | number, payload: ComplianceDecisionPayload) =>
      client.post<ComplianceDecision>(`/app/v1/admin/compliance/withdrawals/${idPath(id)}/decisions`, payload),
    postWithdrawalMessage: (id: string | number, text: string) =>
      client.post<{ ok: boolean }>(`/app/v1/admin/compliance/withdrawals/${idPath(id)}/messages`, { text }),
    listPendingDecisions: () =>
      client.get<{ data: ComplianceDecision[] }>('/app/v1/admin/compliance/decisions'),
    confirmDecision: (id: string, secondFactor: SecondFactor) =>
      client.post<ComplianceDecision>(`/app/v1/admin/compliance/decisions/${idPath(id)}/confirm`, { secondFactor }),
    rejectDecision: (id: string, payload: ComplianceResolvePayload) =>
      client.post<ComplianceDecision>(`/app/v1/admin/compliance/decisions/${idPath(id)}/reject`, payload),
    cancelDecision: (id: string, payload: ComplianceResolvePayload) =>
      client.post<ComplianceDecision>(`/app/v1/admin/compliance/decisions/${idPath(id)}/cancel`, payload),
    listBlockedAddresses: () =>
      client.get<{ data: ComplianceBlockedAddress[] }>('/app/v1/admin/compliance/addresses'),
    unblockAddress: (id: string | number, payload: { reason: string; secondFactor: SecondFactor }) =>
      client.post<ComplianceDecision>(`/app/v1/admin/compliance/addresses/${idPath(id)}/decisions`, {
        decision: 'UNBLOCK',
        ...payload,
      }),
    listReviewers: () => client.get<{ data: ComplianceReviewer[] }>('/app/v1/admin/compliance/reviewers'),
    setReviewer: (userId: string | number, payload: ComplianceReviewerUpdate) =>
      client.put<{ id: number; complianceReviewer: boolean }>(
        `/app/v1/admin/compliance/reviewers/${idPath(userId)}`,
        payload,
      ),
    listMessages: (withdrawalId: string | number) =>
      client.get<ComplianceMessagesResponse>(`/app/v1/withdrawals/${idPath(withdrawalId)}/compliance-messages`),
    replyMessage: (withdrawalId: string | number, text: string) =>
      client.post<ComplianceMessagesResponse>(`/app/v1/withdrawals/${idPath(withdrawalId)}/compliance-messages`, { text }),
  };
}

export type ComplianceService = ReturnType<typeof createComplianceService>;
